import ReactMarkdown from "react-markdown";
import Tag from "@/components/Tag";
import { ExternalLink } from "@/components/withLink";
import { PGAAssistantIcon } from "@/components/CustomSVGIcons/PGAAssistantIcon";
import { sysColors } from "@/assets/theme/colors";
import CardSkeleton from "@/components/Skeleton/CardSkeleton";
import { markdownComponents } from "@/lib/markdownHelpers";

export interface AiCardProps {
  title?: string;
  text: string;
  className?: string;
  loading?: boolean;
  feedbackUrl?: string;
  icon?: React.ReactNode;
  /** When true, render `text` as markdown. Default keeps plain text for other AI card callers. */
  renderAsMarkdown?: boolean;
}

const FeedbackLink = ({ feedbackUrl }: { feedbackUrl?: string }) => {
  if (!feedbackUrl) return null;

  return (
    <ExternalLink href={feedbackUrl}>
      <span className="text-sys-brand-primary ml-2">Is this helpful?</span>
    </ExternalLink>
  );
};

export default function AiCard({
  title,
  text,
  className = "",
  loading = false,
  feedbackUrl,
  icon,
  renderAsMarkdown = false
}: AiCardProps) {
  const displayTitle = title || "Insights";

  // Plain-text callers keep sm:items-center; markdown notes top-align so multi-line bullets sit cleanly.
  const baseClasses = [
    "w-full",
    "bg-extended-pgasilver-brand-color",
    "rounded-lg",
    "flex",
    "flex-col sm:flex-row",
    renderAsMarkdown ? "items-start" : "items-start sm:items-center",
    "px-6",
    "py-6",
    className
  ].join(" ");

  if (loading) {
    return (
      <div className={baseClasses} data-testid="ai-insights">
        <div className="w-full ml-6">
          <div className="w-full">
            <CardSkeleton rows={2} width={100} />
          </div>
        </div>
      </div>
    );
  }
  if (!text) {
    return null;
  }

  return (
    <div className={baseClasses} data-testid="ai-insights">
      <div className="flex items-center mb-4 sm:mb-0">
        <div className="flex-shrink-0">
          {icon || <PGAAssistantIcon size={48} fill={sysColors["extended-pgasilver-brand-on-color"]} />}
        </div>
        <div className="flex flex-row sm:flex-col flex-shrink-0 ml-2 gap-2 sm:gap-0 items-center sm:items-start">
          <h3 className="text-extended-pgasilver-brand-on-color text-title-large-bold font-serif">{displayTitle}</h3>
          <Tag tagType="highlight" tagStyle="strong" size="small">
            Beta
          </Tag>
        </div>
      </div>

      <div className="text-extended-pgasilver-brand-on-color sm:ml-6 flex-1">
        {renderAsMarkdown ? <ReactMarkdown components={markdownComponents}>{text}</ReactMarkdown> : text}
        <FeedbackLink feedbackUrl={feedbackUrl} />
      </div>
    </div>
  );
}
